import { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { getLogger } from '../utils/logger';

const logger = getLogger('useWelcomeModal');

export const useWelcomeModal = () => {
  const { isAuthenticated, isLoading, user } = useAuth0();
  const [isWelcomeModalOpen, setIsWelcomeModalOpen] = useState(false);
  const wasAuthenticated = useRef(false);
  const hasShownWelcome = useRef(false);
  

  useEffect(() => {
    
    const handleAuthenticationChange = () => {
      // Skip if still loading
      if (isLoading) {
        return;
      }

      // Check if we've already shown welcome to this user
      const welcomeShownKey = `welcomeShown_${user?.sub}`;
      const hasShownWelcomeToThisUser = localStorage.getItem(welcomeShownKey) === 'true';
      
      // User just logged in OR user is authenticated and we haven't shown welcome yet
      if (isAuthenticated && user && !hasShownWelcome.current && !hasShownWelcomeToThisUser) {
        try {
          
          // Check if this looks like a signup (new user)
          // Use robust date comparison to handle Auth0 date inconsistencies
          const isNewUser = (() => {
            if (!user.created_at || !user.updated_at) return false;
            
            try {
              const createdAt = new Date(user.created_at).getTime();
              const updatedAt = new Date(user.updated_at).getTime();
              
              // Consider as new user if timestamps are identical or within 5 seconds
              // This accounts for potential timing differences during user creation
              const timeDifferenceSeconds = Math.abs(updatedAt - createdAt) / 1000;
              return timeDifferenceSeconds <= 5;
            } catch (error) {
              logger.warn('Failed to parse user dates for new user detection', { 
                created_at: user.created_at, 
                updated_at: user.updated_at, 
                error 
              });
              return false;
            }
          })();
          
          
          // Show welcome modal only for brand new users (first time login)
          // Use multiple detection methods to be more reliable
          // Since created_at is not available, use updated_at as fallback
          const isVeryRecentUser = (() => {
            const timestamp = user.created_at || user.updated_at;
            if (!timestamp) return false;
            
            try {
              const timestampMs = new Date(timestamp).getTime();
              const now = new Date().getTime();
              const minutesSinceUpdate = (now - timestampMs) / (1000 * 60);
              
              // Consider as new user if updated within last 10 minutes
              return minutesSinceUpdate <= 10;
            } catch (error) {
              logger.warn('Failed to check if user is very recent', { 
                created_at: user.created_at,
                updated_at: user.updated_at,
                error 
              });
              return false;
            }
          })();
          
          const shouldShowWelcome = isNewUser || isVeryRecentUser;
          
          
          if (shouldShowWelcome) {
            // Add a small delay to ensure the dashboard is fully loaded
            setTimeout(() => {
              setIsWelcomeModalOpen(true);
              hasShownWelcome.current = true;
              // Mark as shown in localStorage so it never shows again for this user
              localStorage.setItem(welcomeShownKey, 'true');
            }, 1000);
          }
          
        } catch (error) {
          logger.error('Failed to check new user status', error);
        }
      }
      
      // User just logged out - reset welcome flag
      if (!isAuthenticated && wasAuthenticated.current) {
        hasShownWelcome.current = false;
      }
      
      // Update the reference
      wasAuthenticated.current = isAuthenticated;
    };

    handleAuthenticationChange();
  }, [isAuthenticated, isLoading, user]);

  const closeWelcomeModal = () => {
    setIsWelcomeModalOpen(false);
  };

  return {
    isWelcomeModalOpen,
    closeWelcomeModal,
  };
}; 